import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { CompareArrowsRounded } from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { Loader } from "components/Loader";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { Field, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { PayInputAuditTrail } from "pages/PayRoll/PayInputs/Shared/AuditTrail";
import { EmptyRow, SimpleTable, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { SettingsCard, SettingsGrid } from "pages/PayRoll/Shared/statutoryConfigUi";
import { VERSION_STATUS_COLORS, versionRange } from "pages/PayRoll/Shared/statutoryConfigData";
import { comparePtVersions, fetchPtVersionHistory } from "./ptConfigApi";
import { usePayrollSettingsCrumbs } from "../../payrollSettingsMenu";

// PT Version History, laid out like the LWF one (shared VersionHistoryPanel):
// Compare versions on top, the locked note, the versions table, then
// "What changed" per version. The data comes from the server:
// GET config/versions/<id>/history/ for each saved version (its changes
// from the version before, saved by / at, used by payrolls) and
// GET config/versions/compare/ for Compare versions. Read-only.

const NOTE = "A version used by a payroll is locked. To change anything, create a new version from the first of a month. The whole month uses the version in force on the 1st, under the employee's site on the last day of the month.";

const show = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "object") return Array.isArray(value) ? value.join(", ") : JSON.stringify(value);
  return String(value);
};

const CompareVersions = ({ domain, companyId, versions }) => {
  // Older on the left, newer on the right (versions are oldest first).
  const [a, setA] = useState(() => String(versions[Math.max(0, versions.length - 2)]?.id ?? ""));
  const [b, setB] = useState(() => String(versions[versions.length - 1]?.id ?? ""));
  const [result, setResult] = useState({ loading: false, error: "", data: null });

  useEffect(() => {
    if (!a || !b || a === b) {
      setResult({ loading: false, error: "", data: null });
      return undefined;
    }
    const controller = new AbortController();
    setResult({ loading: true, error: "", data: null });
    comparePtVersions({ domain, companyId, a, b, signal: controller.signal })
      .then((data) => setResult({ loading: false, error: "", data }))
      .catch((error) => {
        if (!controller.signal.aborted) setResult({ loading: false, error: error.message || "Could not compare the versions.", data: null });
      });
    return () => controller.abort();
  }, [domain, companyId, a, b]);

  const options = versions.map((version) => ({ value: String(version.id), label: `${version.name} · ${version.status}` }));
  const nameOf = (id) => versions.find((version) => String(version.id) === id)?.name;
  const rows = result.data?.changes || [];
  return (
    <SettingsCard title="Compare versions" info="Only the settings that differ are listed." sx={{ boxShadow: "none" }}>
      <SettingsGrid columns={{ xs: "1fr", sm: "1fr 1fr" }} sx={{ mb: 1.5 }}>
        <Field label="From">
          <SelectField value={a} onChange={(value) => value && setA(value)} options={options} />
        </Field>
        <Field label="To">
          <SelectField value={b} onChange={(value) => value && setB(value)} options={options} />
        </Field>
      </SettingsGrid>
      {result.error ? (
        <RequestInfoBanner tone="danger" body={result.error} />
      ) : (
        <SimpleTable
          fit
          heads={[
            { label: "Setting", width: 170 },
            { label: nameOf(a) || "From", width: 160 },
            { label: nameOf(b) || "To", width: 160 },
          ]}
        >
          {a === b ? (
            <EmptyRow colSpan={3} text="Pick two different versions." />
          ) : result.loading ? (
            <EmptyRow colSpan={3} text="Comparing…" />
          ) : rows.length ? (
            rows.map((row, index) => (
              <Mui.TableRow key={`${row.field}-${row.label}-${index}`}>
                <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{row.label}</Mui.TableCell>
                <Mui.TableCell sx={tableCellSx}>{show(row.before)}</Mui.TableCell>
                <Mui.TableCell sx={{ ...tableCellSx, color: row.after === "Removed" ? "#DC2626" : undefined }}>{show(row.after)}</Mui.TableCell>
              </Mui.TableRow>
            ))
          ) : (
            <EmptyRow colSpan={3} text="No differences." />
          )}
        </SimpleTable>
      )}
    </SettingsCard>
  );
};

// selected (one version's history) -> an entry of the shared audit trail.
const entryOf = (version, selected) => ({
  id: version.id,
  title: `${version.name} · ${versionRange(version)}`,
  at: selected?.saved_at || null,
  actor: { name: selected?.saved_by || "–" },
  tags: [{ label: version.status, tone: version.status === "Active" ? "green" : "slate" }],
  changes: (selected?.changes || []).map((change) =>
    change.before == null || change.before === "" ? { label: change.label, value: show(change.after) } : { label: change.label, from: show(change.before), to: show(change.after) },
  ),
  details: [{ label: "Used by payroll", value: (selected?.used_by || []).join(", ") || "Not used yet" }],
});

// versionId: any saved version of the site (its history lists them all).
// draft: the site's unsaved draft, if any (named under the table, like LWF).
export const PtVersionHistoryPanel = ({ open, domain, companyId, versionId, draft, subtitle, onClose, onOpenVersion }) => {
  const settingsCrumbs = usePayrollSettingsCrumbs();
  const [load, setLoad] = useState({ loading: true, error: "", siteName: "", versions: [], details: {} });
  const [comparing, setComparing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Opens as a side panel; the header button expands it to full view.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!open) return;
    setComparing(false);
    setExpanded(false);
  }, [open]);

  // The site's versions, then each one's history (changes, saved by, used by).
  useEffect(() => {
    if (!open || versionId == null) return undefined;
    const controller = new AbortController();
    const signal = controller.signal;
    setLoad((current) => ({ ...current, loading: true, error: "" }));
    (async () => {
      const first = await fetchPtVersionHistory({ domain, companyId, versionId, signal });
      const versions = [...first.versions].sort((x, y) => String(x.effective_from).localeCompare(String(y.effective_from)));
      const answers = await Promise.all(
        versions.map((version) =>
          String(version.id) === String(first.selected.id)
            ? first.selected
            : fetchPtVersionHistory({ domain, companyId, versionId: version.id, signal })
                .then((data) => data.selected)
                .catch((error) => {
                  if (signal.aborted) throw error;
                  return null;
                }),
        ),
      );
      const details = Object.fromEntries(versions.map((version, index) => [String(version.id), answers[index]]));
      setLoad({ loading: false, error: "", siteName: first.site_name, versions, details });
    })().catch((error) => {
      if (!signal.aborted) setLoad((current) => ({ ...current, loading: false, error: error.message || "Could not load the version history." }));
    });
    return () => controller.abort();
  }, [open, domain, companyId, versionId, reloadKey]);

  const { versions, details, siteName } = load;
  const usedBy = (version) => details[String(version.id)]?.used_by || [];
  const entries = versions.map((version) => entryOf(version, details[String(version.id)])).reverse();

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      // The panel only shows while open (an expanded panel would stay up after closing).
      expanded={open && expanded}
      onToggleExpand={() => setExpanded((value) => !value)}
      // Payroll -> Payroll home; Professional Tax / site -> back to the site (closes this panel).
      breadcrumb={[
        ...settingsCrumbs("Statutory"),
        { label: "Professional Tax", onClick: onClose },
        ...(siteName ? [{ label: siteName, onClick: onClose }] : []),
        "Version History",
      ]}
      title="Version History"
      subtitle={siteName ? `${siteName} · PT rules by month. A version used by a payroll is locked.` : subtitle}
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
          {versions.length > 1 ? (
            <SecondaryButton startIcon={<CompareArrowsRounded />} onClick={() => setComparing((value) => !value)}>
              {comparing ? "Hide comparison" : "Compare versions"}
            </SecondaryButton>
          ) : null}
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </Mui.Stack>
      }
    >
      {versionId == null ? (
        <RequestInfoBanner body="No version has been saved for this site yet. Save Version 1 to start its history." />
      ) : load.loading && !versions.length ? (
        <Mui.Box role="status" aria-label="Loading version history">
          <Loader info="Loading..." minHeight="40vh" />
        </Mui.Box>
      ) : load.error && !versions.length ? (
        <RequestInfoBanner
          tone="danger"
          title="Couldn't load the version history."
          body={load.error}
          action={<SecondaryButton onClick={() => setReloadKey((key) => key + 1)} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
        />
      ) : (
        <Mui.Stack spacing={2}>
          {comparing ? <CompareVersions domain={domain} companyId={companyId} versions={versions} /> : null}
          <RequestInfoBanner body={NOTE} />
          <SimpleTable
            fit
            heads={[
              { label: "Version", width: 110 },
              { label: "Range", width: 200 },
              { label: "Status", width: 100 },
              { label: "Used by payroll", width: 180 },
              { label: "", width: 80 },
            ]}
          >
            {[...versions].reverse().map((version) => (
              <Mui.TableRow key={version.id} selected={String(version.id) === String(versionId)}>
                <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{version.name}</Mui.TableCell>
                <Mui.TableCell sx={tableCellSx}>{versionRange(version)}</Mui.TableCell>
                <Mui.TableCell sx={tableCellSx}>
                  <RequestStatusChip status={version.status} colorCode={VERSION_STATUS_COLORS[version.status]} />
                </Mui.TableCell>
                <Mui.TableCell sx={tableCellSx}>{usedBy(version).join(", ") || "Not used yet"}</Mui.TableCell>
                <Mui.TableCell sx={tableCellSx}>
                  <SecondaryButton onClick={() => onOpenVersion(version.id)} sx={{ minWidth: 0, minHeight: 32, px: 1.5 }}>
                    {usedBy(version).length || version.status === "Superseded" ? "View" : "Open"}
                  </SecondaryButton>
                </Mui.TableCell>
              </Mui.TableRow>
            ))}
          </SimpleTable>
          {draft ? (
            <Mui.Typography sx={designType.helperText}>
              {draft.name} is a draft from {versionRange(draft).split(" →")[0]}. It shows here once it is saved.
            </Mui.Typography>
          ) : null}
          <Mui.Typography sx={{ ...designType.sectionHeading, pt: 1, color: designColors.slate900 }}>What changed</Mui.Typography>
          <PayInputAuditTrail entries={entries} />
        </Mui.Stack>
      )}
    </GlobalSidePanel>
  );
};
